import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
    api,
    setAccessToken,
    clearAccessToken,
    setAuthFailureHandler,
} from '../api/client';
import { authApi } from '../api/endpoints';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [bootstrapping, setBootstrapping] = useState(true);

    // On mount: try to refresh + fetch /auth/me. If we have a valid refresh
    // cookie, the user is logged in; otherwise we land at the login page.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                // Manually attempt a refresh — if no cookie, this 401s and we
                // fall through to the login page.
                const res = await fetch('/api/v1/auth/refresh', {
                    method: 'POST',
                    credentials: 'include',
                });
                if (res.ok) {
                    const data = await res.json();
                    setAccessToken(data.token);
                    const me = await authApi.me();
                    if (!cancelled) setUser(me);
                }
            } catch {
                // ignore — user isn't logged in
            } finally {
                if (!cancelled) setBootstrapping(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Wire the API client's auth-failure callback to clear our state and
    // bounce back to login (the route guard will redirect).
    useEffect(() => {
        setAuthFailureHandler(() => setUser(null));
    }, []);

    const login = useCallback(async (email, password) => {
        const data = await authApi.login(email, password);
        setAccessToken(data.token);
        setUser(data.user);
        return data.user;
    }, []);

    const logout = useCallback(async () => {
        try { await authApi.logout(); } catch {}
        clearAccessToken();
        setUser(null);
    }, []);

    // Re-fetch /me — used after password change to update the cached
    // must_change_password flag so RequireAuth stops redirecting to /change-password.
    const refreshUser = useCallback(async () => {
        try {
            const me = await authApi.me();
            setUser(me);
            return me;
        } catch {
            return null;
        }
    }, []);

    const value = { user, bootstrapping, login, logout, refreshUser };
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
    return ctx;
}
